/**
 * Schedule wizard component
 * Multi-step form for creating new recurring schedules
 * Reuses LocationStep and OptionsStep from the job wizard
 */

import { useState } from 'react';
import { Card, Button, Space } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CronExpressionParser } from 'cron-parser';
import { StepIndicator } from '@/components/new-job/StepIndicator';
import { ScheduleQueryStep } from './ScheduleQueryStep';
import { LocationStep } from '@/components/new-job/LocationStep';
import { OptionsStep } from '@/components/new-job/OptionsStep';
import { ScheduleStep } from './ScheduleStep';
import { useCreateSchedule } from '@/hooks/useSchedules';
import { boundsSchema, polygonCoordinatesSchema, selectionTypeSchema } from '@/utils/validators';
import { zoomToCellSize } from '@/utils/mapUtils';
import type { CreateScheduleInput, IntervalPreset } from '@/types/schedule';

/**
 * Form schema for the schedule wizard
 */
const createScheduleFormSchema = z.object({
  // Job fields (reused by LocationStep, OptionsStep)
  searchQuery: z.string().min(2).max(500).trim(),
  locationName: z.string().max(255).optional(),
  bounds: boundsSchema,
  polygon: polygonCoordinatesSchema.optional(),
  selectionType: selectionTypeSchema.default('rectangle'),
  zoomLevel: z.number().min(14).max(18).default(16),
  maxResultsPerCell: z.number().min(20).max(120).default(120),
  gridOverlapPercent: z.number().min(0).max(30).default(10),
  includeDetails: z.boolean().default(true),
  // Schedule-specific fields
  name: z.string().min(1, 'Schedule name is required').max(255),
  intervalPreset: z.enum(['daily', 'weekly', 'biweekly', 'monthly', 'custom']),
  cronExpression: z.string().max(100).optional(),
}).refine((data) => {
  if (data.intervalPreset === 'custom') {
    if (!data.cronExpression) return false;
    try {
      CronExpressionParser.parse(data.cronExpression);
      return true;
    } catch {
      return false;
    }
  }
  return true;
}, {
  message: 'A valid cron expression is required for custom intervals',
  path: ['cronExpression'],
});

export type ScheduleFormData = z.infer<typeof createScheduleFormSchema>;

type WizardStep = 'query' | 'location' | 'options' | 'schedule';

const STEPS: { key: WizardStep; title: string; description: string }[] = [
  { key: 'query', title: 'Search Query', description: 'What to search for' },
  { key: 'location', title: 'Location', description: 'Define the search area' },
  { key: 'options', title: 'Options', description: 'Configure settings' },
  { key: 'schedule', title: 'Schedule', description: 'Set timing & name' },
];

export function ScheduleWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const createSchedule = useCreateSchedule();

  const methods = useForm<ScheduleFormData>({
    resolver: zodResolver(createScheduleFormSchema),
    defaultValues: {
      searchQuery: '',
      name: '',
      bounds: undefined,
      zoomLevel: 16,
      maxResultsPerCell: 120,
      gridOverlapPercent: 10,
      includeDetails: true,
      intervalPreset: 'weekly',
      cronExpression: '',
    },
    mode: 'onChange',
  });

  const { handleSubmit, watch, trigger } = methods;

  // Watch only the specific fields needed for step validation
  const searchQuery = watch('searchQuery');
  const bounds = watch('bounds');
  const scheduleName = watch('name');
  const intervalPreset = watch('intervalPreset');

  const currentStepKey = STEPS[currentStep]!.key;
  const isLastStep = currentStep === STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  const isCurrentStepValid = (() => {
    switch (currentStepKey) {
      case 'query': return !!searchQuery?.trim();
      case 'location': return !!bounds;
      case 'options': return true;
      case 'schedule': return !!scheduleName?.trim() && !!intervalPreset;
      default: return false;
    }
  })();

  const handleNext = async () => {
    const fieldsToValidate: (keyof ScheduleFormData)[] = {
      query: ['searchQuery'],
      location: ['bounds'],
      options: ['zoomLevel', 'maxResultsPerCell', 'gridOverlapPercent'],
      schedule: ['name', 'intervalPreset', 'cronExpression'],
    }[currentStepKey] as (keyof ScheduleFormData)[];

    if (fieldsToValidate.length > 0) {
      const isValid = await trigger(fieldsToValidate);
      if (!isValid) return;
    }

    if (!isLastStep) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleCancel = () => {
    navigate('/schedules');
  };

  const onSubmit = async (data: ScheduleFormData) => {
    const input: CreateScheduleInput = {
      name: data.name,
      searchQuery: data.searchQuery,
      locationName: data.locationName,
      bounds: data.bounds,
      polygon: data.polygon,
      selectionType: data.selectionType,
      cellSizeKm: zoomToCellSize(data.zoomLevel),
      zoomLevel: data.zoomLevel,
      maxResultsPerCell: data.maxResultsPerCell,
      gridOverlapPercent: data.gridOverlapPercent,
      includeDetails: data.includeDetails,
      intervalPreset: data.intervalPreset as IntervalPreset,
      cronExpression: data.intervalPreset === 'custom' ? data.cronExpression : undefined,
    };

    try {
      await createSchedule.mutateAsync(input);
      navigate('/schedules');
    } catch {
      // Error handled by hook
    }
  };

  const renderStep = () => {
    switch (currentStepKey) {
      case 'query':
        return <ScheduleQueryStep />;
      case 'location':
        return <LocationStep />;
      case 'options':
        return <OptionsStep />;
      case 'schedule':
        return <ScheduleStep />;
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <Card
        title="Create New Schedule"
        styles={{ body: { padding: '24px 32px' } }}
      >
        <StepIndicator
          steps={STEPS}
          currentStep={currentStep}
          onStepClick={(step) => {
            if (step < currentStep) {
              setCurrentStep(step);
            }
          }}
        />

        <div style={{ minHeight: 400, marginTop: 32, marginBottom: 32 }}>
          {renderStep()}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--color-border)',
            paddingTop: 24,
          }}
        >
          <Button onClick={handleCancel}>Cancel</Button>

          <Space size={12}>
            {!isFirstStep && (
              <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                Back
              </Button>
            )}

            {isLastStep ? (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleSubmit(onSubmit)}
                loading={createSchedule.isPending}
              >
                Create Schedule
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={handleNext}
                disabled={!isCurrentStepValid}
              >
                Next <ArrowRightOutlined />
              </Button>
            )}
          </Space>
        </div>
      </Card>
    </FormProvider>
  );
}

export default ScheduleWizard;
